// 通用 helper：从 StationAddStepDevice.vue 抽出，均为纯函数（无组件状态依赖）
// 依赖：dayjs（保持与原 SFC 一致）

import dayjs from 'dayjs'

/**
 * 生成 `${prefix}_${timestamp}_${random}` 形式的短 id。
 * 用于给列表项做前端稳定 key（不依赖后端 id 是否已生成）。
 */
export function genSimpleId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
}

/** 前端 key 生成器（保持与原代码同前缀，避免历史数据 key 冲突判断偏差） */
export function newEblockKey() {
  return `eb_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export function newMeteringMeterKey() {
  return `mm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export function newWaterMachineKey() {
  return `wm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/**
 * 从字符串中按正则捕获一个正整数序号。捕获组 1 必须为整数。
 * @returns {number} 捕获成功且为正整数则返回该数，否则返回 0
 */
export function parsePositiveOrder(value, pattern) {
  const matched = String(value ?? '').trim().match(pattern)
  if (!matched) return 0
  const order = Number(matched[1])
  return Number.isInteger(order) && order > 0 ? order : 0
}

/**
 * 遍历 list，用 resolver 提取每个 item 的序号，返回「当前最大序号 + 1」。
 * 用于生成"下一个可用序号"。
 */
export function nextOrderFromList(list, resolver) {
  const orders = (Array.isArray(list) ? list : [])
    .map((item, index) => Number(resolver(item, index)) || 0)
    .filter((order) => Number.isInteger(order) && order > 0)
  return (orders.length ? Math.max(...orders) : 0) + 1
}

/**
 * 从 eBlock 名称推断其序号。
 * - 新格式 "N-M#eBlock"：取 # 前的最后一个数字（M 为 eBlock 自身序号）
 * - 旧格式或无 # 时：取开头数字
 */
export function inferEblockSeq(name, fallback = 1) {
  const str = String(name ?? '').trim()
  const hashMatched = str.match(/(\d+)#/)
  if (hashMatched) return Number(hashMatched[1]) || fallback
  const leadingMatched = str.match(/^(\d+)/)
  return leadingMatched ? Number(leadingMatched[1]) || fallback : fallback
}

/** 将 Blob/ArrayBuffer 触发浏览器下载 */
export function saveBlobFile(data, filename) {
  const blob = data instanceof Blob ? data : new Blob([data])
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** 从上传接口响应中拿到文件 URL（兼容多种返回格式） */
export function normalizeUploadedFileUrl(res) {
  const payload = res?.data ?? res
  if (typeof payload === 'string') return payload
  if (!payload || typeof payload !== 'object') return ''
  return payload?.url
}

/** 变压器出厂日期展示格式化：合法日期返回 YYYY年MM月DD日，否则原样返回字符串 */
export function formatTransformerManufactureTime(value) {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('YYYY年MM月DD日') : String(value)
}
